import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Container, FlexColumn, FlexRow } from 'components/common/Containers'
import { AiOutlineCloudUpload } from 'react-icons/ai'
import styles from './BulkUploadModalContent.module.scss'
import { Button } from 'components/common/Buttons/Button'
import { AxiosError } from 'axios'
import { UseMutateAsyncFunction } from 'react-query'

const examples = [
  {
    name: '.csv',
    href: '/docs/domain-availability-examples/example.csv',
  },
  {
    name: '.xls',
    href: '/docs/domain-availability-examples/example.xls',
  },
  {
    name: '.txt',
    href: '/docs/domain-availability-examples/example.txt',
  },
]

export interface Props {
  setModalVisible: React.Dispatch<React.SetStateAction<boolean>>
  setTaskID: React.Dispatch<React.SetStateAction<string>>
  handleUpload: UseMutateAsyncFunction<any, AxiosError<any, any>, File, unknown>
  isUploading: boolean
}

const instructionsData = [
  'One domain per row(.xls or csv file)',
  'One domain name per line(.txt file)',
  'Only *.csv, .xls and *.txt files will be accepted',
]
const BulkUploadModalContent: React.FC<Props> = ({
  setModalVisible,
  setTaskID,
  handleUpload,
  isUploading,
}) => {
  const [files, setFiles] = useState([])
  const [buttonsDisabled, setButtonsDisabled] = useState(false)

  const { acceptedFiles, getRootProps, getInputProps, open, inputRef } =
    useDropzone({
      noClick: true,
      noKeyboard: true,
      maxFiles: 1,
      multiple: false,
      accept: {
        'text/csv': [],
        'text/plain': [],
        'application/vnd.ms-excel': [],
      },
      onDrop: (files) => setFiles(files),
    })

  const removeFile = () => {
    inputRef.current.value = ''
    acceptedFiles.length = 0
    acceptedFiles.splice(0, acceptedFiles.length)
    setFiles([])
  }

  const handleSubmitDomains = () => {
    setButtonsDisabled(true)
    handleUpload(files[0]).then((data) => {
      setTaskID(data.task_id)
    })
  }

  return (
    <Container>
      <FlexColumn gap="1rem">
        <div className={styles.uploadContainer} {...getRootProps()}>
          <input {...getInputProps()} />
          {files.length === 0 && (
            <>
              <AiOutlineCloudUpload size={150} className={styles.uploadIcon} />
              <span>Drop a file here</span>
              <br />
              <span>or</span>
              <br />
              <Button
                outlined
                onClick={open}
                className={styles.selectFileButton}
              >
                Select File
              </Button>
            </>
          )}
          {files.length !== 0 && (
            <>
              <div className={styles.uploadedFilesContainer}>
                <span>Selected File:</span>
                <br />
                <span>{files[0].name}</span>
                <br />

                <Button danger onClick={removeFile}>
                  Remove
                </Button>
              </div>
            </>
          )}
        </div>

        <div className={styles.instructionsContainer}>
          <ul>
            {instructionsData.map((instruction, index) => (
              <li key={index}>{instruction}</li>
            ))}
          </ul>
        </div>

        <div className={styles.downloadTemplateContainer}>
          <span>Download a file template</span>
          <FlexRow justify="center">
            {examples.map((extension, index) => (
              <a
                key={index}
                href={extension.href}
                download
                target="_blank"
                rel="noreferrer"
                className={styles.extensionChip}
              >
                <div>{extension.name}</div>
              </a>
            ))}
          </FlexRow>
        </div>

        <FlexRow className={styles.buttonRow}>
          <Button
            mode="default"
            onClick={() => setModalVisible(false)}
            disabled={buttonsDisabled}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmitDomains} loading={isUploading}>
            Submit Domains
          </Button>
        </FlexRow>
      </FlexColumn>
    </Container>
  )
}

export default BulkUploadModalContent
